import { Link } from "react-router-dom";
import { useContent } from "../../state/siteContent";
import { openLeadModal } from "../LeadModal";

export default function Footer() {
  const year = new Date().getFullYear();
  const { settings, footer } = useContent();
  const office = settings.office;
  const mapHref = office.mapQuery
    ? `https://www.google.com/maps/search/?api=1&query=${office.mapQuery}`
    : null;

  return (
    <footer className="site-footer">
      {/* Closing CTA band — the site's last "do this next" moment, echoing
          the About page's own closing panel so every path through the site
          ends on the same note rather than trailing off into a link list. */}
      <div className="site-footer__cta">
        <div className="site-footer__cta-inner">
          <div>
            <h2 className="site-footer__cta-title">{footer.ctaTitle}</h2>
            <p className="site-footer__cta-line">{footer.ctaLine}</p>
          </div>
          <button type="button" className="site-footer__cta-btn" onClick={openLeadModal}>
            {footer.ctaButton}
          </button>
        </div>
      </div>

      <div className="site-footer__grid">
        <div className="site-footer__brand-col">
          <span className="brand-chip site-footer__brand-chip">
            <img
              src={settings.logoMark}
              alt={settings.brandName}
              className="site-footer__logo"
              width="209"
              height="68"
              loading="lazy"
              decoding="async"
            />
          </span>
          <p className="site-footer__brand-line">
            {footer.brandLine}
          </p>
        </div>

        <div className="site-footer__col">
          <div className="site-footer__col-title">Explore</div>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/about">About Us</Link></li>
            <li><Link to="/projects">Projects</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>

        <div className="site-footer__col">
          <div className="site-footer__col-title">Office</div>
          {/* Address itself is the map link now (was a separate "View on
              map" button below — removed to save vertical space); the whole
              block opens Google Maps at the exact pinned location. */}
          {mapHref ? (
            <a
              href={mapHref}
              target="_blank"
              rel="noreferrer"
              className="site-footer__address-link"
              aria-label={`${office.line1 || "Office address"} — view on Google Maps`}
            >
              <ul>
                {office.line1 && <li>{office.line1}</li>}
                {office.line2 && <li>{office.line2}</li>}
                {office.line3 && <li>{office.line3}</li>}
                {office.line4 && <li>{office.line4}</li>}
              </ul>
            </a>
          ) : (
            <ul>
              {office.line1 && <li>{office.line1}</li>}
              {office.line2 && <li>{office.line2}</li>}
              {office.line3 && <li>{office.line3}</li>}
              {office.line4 && <li>{office.line4}</li>}
            </ul>
          )}
        </div>

        <div className="site-footer__col">
          <div className="site-footer__col-title">Connect</div>
          <ul>
            <li><a href={`mailto:${settings.email}`}>{settings.email}</a></li>
            <li>
              <a href={`tel:${settings.phoneRaw}`}>{settings.phone}</a>
              <span className="site-footer__line-tag">Enquiries</span>
            </li>
            {settings.hrPhone && (
              <li>
                <a href={`tel:${settings.hrPhoneRaw}`}>{settings.hrPhone}</a>
                <span className="site-footer__line-tag">Careers &amp; Admin</span>
              </li>
            )}
          </ul>
          <div className="site-footer__social" aria-label="Social media">
            {settings.instagram && (
              <a
                href={settings.instagram}
                target="_blank"
                rel="noreferrer"
                className="site-footer__social-link"
                aria-label="Instagram"
                title="Instagram"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" strokeWidth="1.6" />
                  <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.6" />
                  <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" />
                </svg>
              </a>
            )}
            {settings.linkedin && (
              <a
                href={settings.linkedin}
                target="_blank"
                rel="noreferrer"
                className="site-footer__social-link"
                aria-label="LinkedIn"
                title="LinkedIn"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM9 9h3.8v1.7h.05c.53-.95 1.83-1.95 3.77-1.95C20.6 8.75 22 10.9 22 14.1V21h-4v-6.1c0-1.45-.03-3.32-2.02-3.32-2.02 0-2.33 1.58-2.33 3.21V21H9z" />
                </svg>
              </a>
            )}
            {settings.facebook && (
              <a
                href={settings.facebook}
                target="_blank"
                rel="noreferrer"
                className="site-footer__social-link"
                aria-label="Facebook"
                title="Facebook"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H17V3.6c-.29-.04-1.27-.12-2.41-.12-2.39 0-4.02 1.46-4.02 4.13V9.9H7.8V13h2.77v8z" />
                </svg>
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="site-footer__bottom">
        <p className="site-footer__disclaimer">
          This document is the exclusive property of Nausiddhi Realty. All rights reserved. Unauthorized reproduction or distribution is strictly prohibited. Images used are AI-generated and/or sourced reference visuals conveying the project's concept and design direction — as the project is under development, all visuals are indicative only, subject to change, and not a guarantee of the final appearance.
        </p>
        <div className="site-footer__bottom-row">
          <span>© {year} {settings.brandName} · All rights reserved</span>
          <span className="site-footer__bottom-right">
            <span className="site-footer__credit">Developed by Mage Marketer</span>
          </span>
        </div>
      </div>
    </footer>
  );
}
